import { useState } from 'react'
import { Modal } from '../Common/Modal'
import { Button } from '../Common/Button'

export function PolicyImport({ onImport, onClose, sidebarCollapsed, isDarkMode = false }) {
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [columnMap, setColumnMap] = useState({})
  const [dragOver, setDragOver] = useState(false)
  const [importResult, setImportResult] = useState(null) // { imported, updated }
  const [progress, setProgress] = useState(null) // { current, total, phase }

  const AUTO_MAP = {
    // Policy number
    'policy number': 'policy_number',
    'policy no': 'policy_number',
    'policy no.': 'policy_number',
    'policynumber': 'policy_number',
    // Provider
    'product provider': 'product_provider',
    'provider': 'product_provider',
    'supplier': 'product_provider',
    // Product / category
    'product': 'product',
    'category': 'category',
    'type': 'category',
    'agenttype': 'category',
    'agent type': 'category',
    'sub category': 'product',
    'subcategory': 'product',
    // Lookup fields
    'entity': 'entity',
    'client': 'client',
    // Customer code
    'customercode': 'customer_code',
    'customer code': 'customer_code',
    'customer_code': 'customer_code',
    'cust. code': 'customer_code',
    'cust code': 'customer_code',
    'client code': 'customer_code',
    'client_code': 'customer_code',
    // Other policy fields
    'group': 'group',
    'notes': 'notes',
    'advisor count': 'advisor_count',
    'advisors': 'advisor_count',
    'active': 'active',
    // Dates
    'startdate': 'start_date',
    'start date': 'start_date',
    'start_date': 'start_date',
    'inceptiondate': 'start_date',
    'inception date': 'start_date',
    'enddate': 'end_date',
    'end date': 'end_date',
    'end_date': 'end_date',
    'expirydate': 'end_date',
    'expiry date': 'end_date',
    'maturitydate': 'end_date',
    // Commission split — single unnumbered % column
    '%com': '_pct_1',
    '%comm': '_pct_1',
    'commission': '_pct_1',
    'comm': '_pct_1',
    'commission %': '_pct_1',
    'comm %': '_pct_1',
    '% commission': '_pct_1',
  }

  const processFile = async (selectedFile) => {
    if (!selectedFile) return
    setError('')
    setFile(selectedFile)
    setLoading(true)
    try {
      const XLSX = await import('xlsx')
      const reader = new FileReader()
      reader.onload = (event) => {
        try {
          const data = event.target?.result
          const workbook = XLSX.read(data, { type: 'binary' })
          const worksheet = workbook.Sheets[workbook.SheetNames[0]]
          const rows = XLSX.utils.sheet_to_json(worksheet)
          if (rows.length === 0) { setError('No data found in file'); setLoading(false); return }

          const headers = Object.keys(rows[0])
          const newMap = {}
          headers.forEach(header => {
            const key = header.toLowerCase().trim()
            const matched = AUTO_MAP[key]
            if (matched) { newMap[header] = matched; return }
            // Numbered advisor/split columns: "Advisor 1", "Sales Rep 1", "Adviser 1", "% 1", "Split 1%"
            const advisorMatch = key.match(/^(?:advisor|adviser|sales\s*rep(?:resentative)?|rep)\s*(\d+)$/)
            const pctMatch = key.match(/^%\s*(\d+)$/) || key.match(/^split\s*(\d+)%?$/)
            if (advisorMatch) { newMap[header] = `_advisor_${advisorMatch[1]}`; return }
            if (pctMatch) { newMap[header] = `_pct_${pctMatch[1]}`; return }
            // Single (unnumbered) advisor column: "Sales Rep", "Adviser", "Rep"
            if (/^(?:sales\s*rep(?:resentative)?|adviser|rep)$/.test(key)) newMap[header] = '_advisor_1'
          })
          setColumnMap(newMap)
          setPreview(rows.slice(0, 5))
        } catch (err) {
          setError(`Error reading file: ${err.message}`)
        } finally {
          setLoading(false)
        }
      }
      reader.onerror = () => { setError('Error reading file'); setLoading(false) }
      reader.readAsBinaryString(selectedFile)
    } catch (err) {
      setError(`Error loading Excel library: ${err.message}`)
      setLoading(false)
    }
  }

  const handleFileSelect = (e) => {
    processFile(e.target.files?.[0])
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = () => {
    setDragOver(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    processFile(e.dataTransfer.files?.[0])
  }

  const handleImport = async () => {
    if (!file || !preview) { setError('No file selected'); return }
    setError('')
    setLoading(true)
    try {
      const XLSX = await import('xlsx')
      const reader = new FileReader()
      reader.onload = async (event) => {
        try {
          const data = event.target?.result
          const workbook = XLSX.read(data, { type: 'binary' })
          const worksheet = workbook.Sheets[workbook.SheetNames[0]]
          const rows = XLSX.utils.sheet_to_json(worksheet)

          // Helper: convert Excel serial date number → ISO string (YYYY-MM-DD)
          const excelDateToISO = (val) => {
            if (!val) return null
            const n = Number(val)
            if (!isNaN(n) && n > 1000) {
              // Excel epoch: Jan 1 1900 = day 1 (with leap year bug offset)
              const d = new Date((n - 25569) * 86400 * 1000)
              return d.toISOString().split('T')[0]
            }
            // Already a string date
            const d = new Date(val)
            return isNaN(d) ? null : d.toISOString().split('T')[0]
          }

          const mapped = rows.map(row => {
            const out = {}
            Object.entries(columnMap).forEach(([excelCol, dbCol]) => {
              if (!dbCol || dbCol === 'skip' || row[excelCol] === undefined) return
              if (dbCol === 'active') {
                out[dbCol] = row[excelCol] === true || row[excelCol] === 'TRUE' || row[excelCol] === 'Yes' || row[excelCol] === 1
              } else if (dbCol === 'advisor_count') {
                out[dbCol] = Number(row[excelCol]) || 0
              } else if (dbCol === 'start_date' || dbCol === 'end_date') {
                out[dbCol] = excelDateToISO(row[excelCol])
              } else if (dbCol.startsWith('_advisor_') || dbCol.startsWith('_pct_')) {
                out[dbCol] = row[excelCol]
              } else {
                out[dbCol] = String(row[excelCol]).trim()
              }
            })
            // Bundle _advisor_N / _pct_N into _advisorSplits (support unlimited advisors)
            const advisorSplits = []
            for (let n = 1; n <= 50; n++) {
              const name = out[`_advisor_${n}`]
              const pct = out[`_pct_${n}`]
              if (name && String(name).trim()) {
                // If pct looks like a decimal (e.g. 0.05 = 5%), convert to percentage
                let pctVal = pct != null ? Number(pct) : null
                if (pctVal != null && pctVal > 0 && pctVal < 1) pctVal = Math.round(pctVal * 100 * 100) / 100
                advisorSplits.push({ name: String(name).trim(), pct: pctVal })
              }
              delete out[`_advisor_${n}`]
              delete out[`_pct_${n}`]
            }
            if (advisorSplits.length) out._advisorSplits = advisorSplits
            return out
          }).filter(row => row.policy_number)

          if (mapped.length === 0) {
            const mappedCols = Object.values(columnMap).filter(v => v && v !== 'skip')
            const hasPolicyNumber = mappedCols.includes('policy_number')
            setError(hasPolicyNumber
              ? `No valid rows found. Check that Policy Number column has values.`
              : `No valid rows found. You must map and select the Policy Number column.`)
            setLoading(false)
            return
          }

          const result = await onImport(mapped, (prog) => setProgress(prog))
          setImportResult(result || { imported: mapped.length, updated: 0 })
          setProgress(null)
          setLoading(false)
        } catch (err) {
          setError(`Import error: ${err.message}`)
          setLoading(false)
        }
      }
      reader.onerror = () => { setError('Error reading file'); setLoading(false) }
      reader.readAsBinaryString(file)
    } catch (err) {
      setError(`Error: ${err.message}`)
      setLoading(false)
    }
  }

  const excelColumns = preview ? Object.keys(preview[0]) : []
  const isImporting = loading && !!preview

  return (
    <Modal isOpen={true} onClose={onClose} title="Import Policies" sidebarCollapsed={sidebarCollapsed} isDarkMode={isDarkMode}>
      <div className="space-y-4 max-h-[80vh] flex flex-col">

        {/* Full-panel loader shown while the actual import is running */}
        {isImporting && (
          <div className="flex flex-col items-center justify-center py-16 gap-5">
            <div className="relative">
              <div className="w-16 h-16 rounded-full border-4 border-orange-100 border-t-sfp-orange animate-spin" />
            </div>
            <div className="text-center">
              <p className={`text-base font-semibold ${isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'}`}>
                Importing policies…
              </p>
              {progress && (
                <div className="mt-4 w-64">
                  <div className="flex justify-between mb-2">
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                      {progress.phase}
                    </span>
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                      {progress.current} / {progress.total}
                    </span>
                  </div>
                  <div className={`w-full h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}>
                    <div
                      className="h-full bg-sfp-orange transition-all duration-300"
                      style={{ width: `${Math.round((progress.current / progress.total) * 100)}%` }}
                    />
                  </div>
                  <p className={`text-xs mt-2 text-center ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    {Math.round((progress.current / progress.total) * 100)}% complete
                  </p>
                </div>
              )}
              <p className={`text-sm mt-3 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                This may take a moment. Please don't close this window.
              </p>
            </div>
          </div>
        )}

        {!isImporting && error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
            {error}
          </div>
        )}

        {!isImporting && importResult ? (
          <div className="flex flex-col items-center justify-center py-10 gap-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <span className="text-3xl">✓</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-800">Import Complete</h3>
            <div className="text-sm text-gray-600 text-center space-y-1">
              {importResult.imported > 0 && <p><span className="font-semibold text-green-600">{importResult.imported}</span> new polic{importResult.imported === 1 ? 'y' : 'ies'} added</p>}
              {importResult.updated > 0 && <p><span className="font-semibold text-blue-600">{importResult.updated}</span> existing polic{importResult.updated === 1 ? 'y' : 'ies'} updated</p>}
              {importResult.unmatched?.length > 0 && (
                <p className="text-amber-600 text-xs mt-2">
                  ⚠ Advisors not matched (check spelling):<br />
                  <span className="font-semibold">{importResult.unmatched.join(', ')}</span>
                </p>
              )}
            </div>
            <Button variant="primary" onClick={onClose}>Done</Button>
          </div>
        ) : !isImporting && !preview ? (
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragOver
                ? 'border-sfp-orange bg-orange-50'
                : 'border-gray-300 hover:border-sfp-orange'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id="policy-file-input"
              name="policy_file_input"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              disabled={loading}
              className="hidden"
            />
            <label htmlFor="policy-file-input" className="cursor-pointer">
              <p className="text-lg font-semibold text-sfp-darkgray mb-2">
                📁 Drop file here or click to select
              </p>
              <p className="text-sm text-gray-600">Supported: Excel (.xlsx, .xls) or CSV</p>
              {loading && <p className="text-sm text-sfp-orange mt-2">Loading...</p>}
            </label>
          </div>
        ) : !isImporting ? (
          <div className="space-y-4 flex-1 overflow-y-auto">
            <div>
              <h3 className="font-semibold text-sm text-sfp-darkgray mb-3">
                Map Excel Columns to Database Fields
              </h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {excelColumns.map(col => {
                  const mapped = columnMap[col] || ''
                  const isAutoAdvisor = mapped.startsWith('_advisor_') || mapped.startsWith('_pct_')
                  if (isAutoAdvisor) {
                    return (
                      <div key={col} className="flex gap-2 items-center text-sm">
                        <span className="flex-1 text-gray-700">{col}</span>
                        <span className="text-gray-400">→</span>
                        <span className="flex-1 text-xs text-sfp-orange font-medium bg-orange-50 border border-orange-200 rounded px-2 py-1">
                          Auto → {mapped.startsWith('_advisor_') ? `Advisor ${mapped.split('_')[2]} (name match)` : `Split % ${mapped.split('_')[2]}`}
                        </span>
                      </div>
                    )
                  }
                  return (
                  <div key={col} className="flex gap-2 items-center text-sm">
                    <span className="flex-1 text-gray-700">{col}</span>
                    <span className="text-gray-400">→</span>
                    <select
                      id={`col-map-${col}`}
                      name={`col-map-${col}`}
                      value={mapped}
                      onChange={(e) => setColumnMap(prev => ({ ...prev, [col]: e.target.value }))}
                      className="input-field flex-1 text-sm py-1"
                    >
                      <option value="">Skip</option>
                      <option value="policy_number">Policy Number *</option>
                      <option value="product_provider">Product Provider</option>
                      <option value="category">Category</option>
                      <option value="product">Product</option>
                      <option value="customer_code">Customer Code</option>
                      <option value="group">Group</option>
                      <option value="notes">Notes</option>
                      <option value="entity">Entity</option>
                      <option value="client">Client</option>
                      <option value="start_date">Start Date</option>
                      <option value="end_date">End Date</option>
                      <option value="advisor_count">Advisor Count</option>
                      <option value="active">Active</option>
                    </select>
                  </div>
                  )
                })}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-sm text-sfp-darkgray mb-2">Preview (first 5 rows)</h3>
              <div className="overflow-x-auto border border-gray-200 rounded max-h-40 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="table-header sticky top-0">
                    <tr>
                      {excelColumns.map(col => (
                        <th key={col} className="px-3 py-2 text-left">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'table-row-even' : 'table-row-odd'}>
                        {excelColumns.map(col => (
                          <td key={col} className="px-3 py-2 text-gray-700">
                            {String(row[col] ?? '').substring(0, 50)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mt-1">Showing first 5 rows</p>
            </div>
          </div>
        ) : null}

        {!isImporting && !importResult && (
        <div className="flex gap-2 justify-end pt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose} type="button" disabled={loading}>
            Cancel
          </Button>
          {preview && (
            <>
              <Button
                variant="secondary"
                onClick={() => { setPreview(null); setFile(null); setColumnMap({}) }}
                type="button"
                disabled={loading}
              >
                Back
              </Button>
              <Button variant="primary" onClick={handleImport} disabled={loading}>
                Import Policies
              </Button>
            </>
          )}
        </div>
        )}
      </div>
    </Modal>
  )
}
